import {useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useCurrency } from "context/CurrencyProvider";
import { TrackJS } from "trackjs";
import { useProducts } from "context/ProductProvider";
import { useOffsiteCampaign } from "context/OffsiteCampaignProvider";
import { validateNumber } from "utils/helpers";
import useDrawerVisibility from "hooks/useDrawerVisibility";
import OffsiteAudience from "./OffsiteAudience";
import OffsiteDailyBudget from "./OffsiteDailyBudget";
import OffsiteRedirectUrl from "./OffsiteRedirectUrl";
import OffsiteAdPreview from "./OffsiteAdPreview";
import OffsiteEditAdDrawer from "./OffsiteEditAdDrawer";
import OffsiteAudienceDrawer from "./OffsiteAudienceDrawer";
import SponsoredProducts from "components/Form/RetailCampaignForm/RetailCampaignFormFirstStep/SponsoredProducts";
import useOffsiteApi from "api/offsite";

const RetailOffsiteCampaignFormFirstStep = ({
  advertiser,
}: {
  advertiser: {
    id: number;
    name: string;
  };
}) => {
  const { currencySymbol } = useCurrency();
  const { offSiteApi, baseURL, role, isLimitedUser } = useOffsiteApi();
  const [selectedAudience, setSelectedAudience] = useState<string>("");

  const { selectedProducts } = useProducts();

  const { formFields, setFormFields, handleFormFieldsChange, productError } =
    useOffsiteCampaign();

  const {
    isVisible: editDrawerVisible,
    open: openEditDrawer,
    close: closeEditDrawer,
  } = useDrawerVisibility();

  const {
    isVisible: audienceDrawerVisible,
    open,
    close,
  } = useDrawerVisibility();

  const handleSelectedAudienceChange = (value: string) =>
    setSelectedAudience(value);

  const openAudienceDrawer = () => {
    setSelectedAudience(formFields.audience);
    open();
  };

  const closeAudienceDrawer = () => {
    setSelectedAudience("");
    close();
  };

  const { data: audiencesData, isLoading: isLoadingAudiences } = useQuery(
    ["audiences", advertiser?.id],
    async () => {
      try {
        const response = await offSiteApi.get("/meta/custom-audiences/");
        return {
          data: {
            audiences: response.data,
          },
        };
      } catch (error) {
        TrackJS.track({
          message: "Error fetching audiences",
          metadata: {
            endpoint: "audiences",
            role,
            isLimitedUser: `Is user limited? ${isLimitedUser}`,
            baseURL: baseURL(),
            responseBody: error?.response?.data,
            statusCode: error?.response?.status,
            errorMessage: error?.message,
            severity: "Medium",
          },
        });
        throw error;
      }
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      retry: false,
    }
  );

  const audienceOptions =
    audiencesData?.data?.audiences?.map(
      (audience: { id: string; name: string }) => ({
        value: audience.id,
        label: audience.name,
      })
    ) || [];

  const disableDrawerOkButton =
    formFields.adName === "" || formFields.primaryText === "";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
    validateNumber(e);

  useEffect(() => {
    if (!selectedProducts.length) return;

    const placeholderPrimaryText =
      selectedProducts[0]?.brand && selectedProducts[0]?.name
        ? `${selectedProducts[0]?.brand || ""} ${
            selectedProducts[0]?.name || ""
          }`
        : "-";

    setFormFields({
      ...formFields,
      adName: selectedProducts[0]?.name,
      primaryText: placeholderPrimaryText,
      description: selectedProducts[0]?.description,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProducts]);

  return (
    <>
      <SponsoredProducts
        error={productError}
        isBidColonVisible={false}
        ad_type={"PRODUCT"}
        advertiserStateForLinks={advertiser}
        targeting={""}
        isOffsite={true}
      />

      <OffsiteDailyBudget
        formFields={formFields}
        handleFormFieldsChange={handleFormFieldsChange}
        handleKeyDown={handleKeyDown}
        currencySymbol={currencySymbol}
      />

      <OffsiteRedirectUrl
        formFields={formFields}
        handleFormFieldsChange={handleFormFieldsChange}
      />

      <OffsiteAudience setAudienceDrawerVisible={openAudienceDrawer} />

      <OffsiteAdPreview
        formFields={formFields}
        selectedProducts={selectedProducts}
        audienceOptions={audienceOptions}
        openEditDrawer={openEditDrawer}
      />

      <OffsiteEditAdDrawer
        visible={editDrawerVisible}
        onClose={closeEditDrawer}
        disableOkButton={disableDrawerOkButton}
        formFields={formFields}
        handleFormFieldsChange={handleFormFieldsChange}
      />

      <OffsiteAudienceDrawer
        visible={audienceDrawerVisible}
        onClose={closeAudienceDrawer}
        selectedAudience={selectedAudience}
        audienceOptions={audienceOptions}
        isLoadingAudiences={isLoadingAudiences}
        handleSelectedAudienceChange={handleSelectedAudienceChange}
      />
    </>
  );
};

export default RetailOffsiteCampaignFormFirstStep;
